import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import lefticon from "../images/Leftnew.svg";
import img from "../images/Checkbox.svg";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "",
    profile: {
      specialty: "",
      licenseNumber: "",
      organization: "",
      healthcareProvider: "",
      phone: "",
    },
  });
  const [organizations, setOrganizations] = useState([]);
  const [healthcareProviders, setHealthcareProviders] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchOrganizations();
    fetchHealthcareProviders();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/organizations"
      );
      setOrganizations(res.data);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    }
  };

  const fetchHealthcareProviders = async () => {
    try {
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/healthcare-providers"
      );
      setHealthcareProviders(res.data);
    } catch (err) {
      console.error("Error fetching healthcare providers:", err);
    }
  };

  const handleTermsAcceptance = () => {
    setTermsAccepted(!termsAccepted);
    if (errors.terms) {
      setErrors((prev) => ({ ...prev, terms: "" }));
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (Object.keys(formData.profile).includes(name)) {
      setFormData((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    // Role validation
    if (!formData.role) newErrors.role = "Role is required";

    // Terms acceptance validation
    if (!termsAccepted) {
      newErrors.terms = "You must accept the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // First validate all required fields
      if (
        !formData.email ||
        !formData.password ||
        !formData.firstName ||
        !formData.lastName ||
        !formData.role
      ) {
        throw new Error("Please fill in all required fields");
      }

      // Make the signup request
      const response = await axios.post(
        "https://app.metabolytics.ai/simulator-backend/healthcare/signup",
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          profile: {
            specialty: formData.profile.specialty,
            licenseNumber: formData.profile.licenseNumber,
            organization: formData.profile.organization,
            healthcareProvider: formData.profile.healthcareProvider,
            phone: formData.profile.phone,
          },
        }
      );

      if (response.data && response.data.message) {
        alert(response.data.message);
        // Navigate to verification page with email as query parameter
        navigate(
          `/verificationpage?email=${encodeURIComponent(formData.email)}`
        );
      }
    } catch (error) {
      console.error("Signup error:", error);
      setErrors({
        submit:
          error.response?.data?.message ||
          "Failed to sign up. Please check your information and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="Login-container-bg">
      <div className="Login-container-main">
        <div
          className="Signup-icon-container-new"
          onClick={() => navigate(-1)}
          style={{ cursor: "pointer" }}
        >
          <img src={lefticon} alt="Back" />
        </div>
        <h3 className="signup_button">Sign Up</h3>
        <h6>Let's start with some facts about you</h6>

        <form onSubmit={handleSubmit}>
          <div className="Signup-name-inputs">
            <div className="Signup-first-name-input">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>
            <div className="Signup-last-name-input">
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="Signup-input">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="Signup-input password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
            <span
              className="password-toggle password-toggle-signup"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </span>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          <div className="Signup-input">
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="signup-select"
            >
              <option value="">Select Role</option>
              <option value="Physician">Physician</option>
              <option value="Registered Nurse">Registered Nurse</option>
              <option value="Medical Assistant">Medical Assistant</option>
            </select>
            {errors.role && (
              <span className="error-message">{errors.role}</span>
            )}
          </div>

          <div className="Signup-input">
            <select
              name="organization"
              value={formData.profile.organization}
              onChange={handleChange}
              className="signup-select"
            >
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div className="Signup-input">
            <select
              name="healthcareProvider"
              value={formData.profile.healthcareProvider}
              onChange={handleChange}
              className="signup-select"
            >
              <option value="">Select Healthcare Provider</option>
              {healthcareProviders.map((provider) => (
                <option key={provider._id} value={provider._id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          <div className="Signup-input">
            <input
              type="text"
              name="specialty"
              placeholder="Specialty"
              value={formData.profile.specialty}
              onChange={handleChange}
            />
          </div>

          <div className="Signup-input">
            <input
              type="text"
              name="licenseNumber"
              placeholder="License Number"
              value={formData.profile.licenseNumber}
              onChange={handleChange}
            />
          </div>

          <div className="Signup-input">
            <input
              type="tel"
              name="phone"
              placeholder="Phone"
              value={formData.profile.phone}
              onChange={handleChange}
            />
          </div>
          <div className="Signup-terms-container">
            <div
              className="Signup-terms-icon"
              onClick={handleTermsAcceptance}
              role="checkbox"
              aria-checked={termsAccepted}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleTermsAcceptance();
                }
              }}
            >
              <img
                src={img}
                alt={
                  termsAccepted
                    ? "Terms Accepted"
                    : "Accept Terms and Conditions"
                }
                className={`terms-checkbox ${termsAccepted ? "checked" : ""}`}
              />
            </div>
            <div className="Signup-terms-text">
              <p>
                By creating an account, you agree to our{" "}
                <span>Terms and Conditions</span>
              </p>
            </div>
            {errors.terms && (
              <span className="error-message terms-error">{errors.terms}</span>
            )}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="login-button-container">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Signing Up..." : "Sign Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
